import { Box, Chip, CircularProgress, Pagination, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { getDatum } from "@accredo/fe-shared/services/formatService.js";
import TabList from "@accredo/fe-shared/components/TabList.jsx";

/**
 * Mobile card layout for the Kundenportal's Vertragstabelle - used instead
 * of the shared DataTable/DataList below the "md" breakpoint, as that list
 * view is reused by every table in the app and a contract-specific layout
 * (status badge top-right, tighter spacing) would change all of them.
 */
export default function ContractCardList({
  rows,
  isLoading,
  hasLoaded,
  pageIndex = 0,
  pageSize = 10,
  totalRows = 0,
  onChangePage,
  onRowClick,
  filterTabs,
  activeFilterTab,
  title,
}) {
  const theme = useTheme();
  const pageCount = Math.ceil(totalRows / pageSize) || 1;

  if (!hasLoaded && isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box sx={{ opacity: isLoading ? 0.6 : 1 }}>
      {title && (
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>
          {title}
        </Typography>
      )}

      {filterTabs && filterTabs.length > 0 && (
        <TabList
          tabs={filterTabs}
          activeTab={activeFilterTab}
          onTabChange={(tabKey) => {
            const tab = filterTabs.find((t) => t.key === tabKey);
            tab?.onClick?.(tabKey);
          }}
          sx={{ mb: 1.5 }}
        />
      )}

      <Stack spacing={1}>
        {rows.map((row) => {
          const objekte = row.objekts || [];
          // When the Objekt's name is the primary title, the Vertragsnummer
          // would otherwise not appear on the card at all (unlike the
          // desktop table, which has a separate Vertragsnummer column).
          const shows_objekt_name = objekte.length === 1;
          const primary = shows_objekt_name
            ? objekte[0].bezeichnung
            : row.vertragsnummer;

          return (
            <Box
              key={row.id}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              sx={{
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: theme.spacing(1),
                p: 1.5,
                cursor: onRowClick ? "pointer" : "default",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 1,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {primary}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.vertragsart}
                    {shows_objekt_name && ` · ${row.vertragsnummer}`}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={row.is_active ? "aktiv" : "archiviert"}
                  variant={
                    row.is_active
                      ? "contract-status_aktiv"
                      : "contract-status_archiviert"
                  }
                  sx={{ flexShrink: 0 }}
                />
              </Box>

              {row.sparten?.length > 0 && (
                <Stack
                  direction="row"
                  spacing={0.5}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ mt: 1 }}
                >
                  {row.sparten.map((sparte) => (
                    <Chip key={sparte.id} size="small" label={sparte.sparte} />
                  ))}
                </Stack>
              )}

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                Letzter Nachtrag:{" "}
                {row.letzter_nachtrag ? getDatum(row.letzter_nachtrag) : "–"}
              </Typography>
            </Box>
          );
        })}

        {hasLoaded && rows.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Keine Einträge gefunden.
          </Typography>
        )}
      </Stack>

      {pageCount > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Pagination
            count={pageCount}
            page={pageIndex + 1}
            onChange={(_, value) =>
              onChangePage?.({ pageIndex: value - 1, pageSize })
            }
            color="primary"
            siblingCount={0}
          />
        </Box>
      )}
    </Box>
  );
}
